import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import { initAllLibraries } from './utils/initLibraries';
import urlParamsService from './utils/urlParams'; // Initialize URL parameters service globally

// Import components
import Header from './components/Header';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import ScrollToTop from './components/ScrollToTop';
import ScrollToTopOnRouteChange from './components/ScrollToTopOnRouteChange';
import ApplicationForm from './components/ApplicationForm';
import ProtectedRoute from './components/ProtectedRoute';

// Import pages
import Home from './pages/Home';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import FAQs from './pages/FAQs';
import ApplicationFormPage from './pages/ApplicationForm';
import Login from './pages/Login';
import PaymentPage from './pages/PaymentPage';
import CourseFeesPage from './pages/CourseFeesPage';

// B.Tech pages
import BTechCivilEngineering from './pages/BTechCivilEngineering';
import BTechComputerScienceEngineering from './pages/BTechComputerScienceEngineering';
import BTechElectricalEngineering from './pages/BTechElectricalEngineering';
import BTechElectronicAndCommunicationEngineering from './pages/BTechElectronicAndCommunicationEngineering';
import BTechChemicalEngineering from './pages/BTechChemicalEngineering';
import BTechMechanicalEngineering from './pages/BTechMechanicalEngineering';
import BTechMiningEngineering from './pages/BTechMiningEngineering';

// M.Tech pages
import MTechCivilEngineering from './pages/MTechCivilEngineering';
import MTechComputerScienceEngineering from './pages/MTechComputerScienceEngineering';
import MTechMechanicalEngineering from './pages/MTechMechanicalEngineering';
import MTechElectronicsCommunicationEngineering from './pages/MTechElectronicsCommunicationEngineering';
import MTechElectricalEngineering from './pages/MTechElectricalEngineering';
import MTechChemicalEngineering from './pages/MTechChemicalEngineering';
import MTechMiningEngineering from './pages/MTechMiningEngineering';
import { 
  RecognitionsAchievements
} from './pages/CoursePages';

// Component to handle ApplicationForm popup on route changes
const ApplicationFormHandler = ({ showApplicationModal, setShowApplicationModal }) => {
  const location = useLocation();

  useEffect(() => {
    // Refresh URL parameters on every route change to detect ref parameter changes
    urlParamsService.refreshPartnerId();
    
    // Don't show modal on application-form page or login page
    if (location.pathname === '/application-form' || location.pathname === '/login') {
      setShowApplicationModal(false);
      // Close mobile menu if it's open
      document.body.classList.remove('mobile-nav-active');
      return;
    }

    // Close mobile navigation menu on specific pages
    const autoClosePages = ['/application-form', '/login', '/about-us', '/contact-us', '/'];
    if (autoClosePages.includes(location.pathname)) {
      document.body.classList.remove('mobile-nav-active');
    }

    // Don't show modal if user is authenticated
    const isAuthenticated = sessionStorage.getItem('_auth_token_') !== null;
    if (isAuthenticated) {
      setShowApplicationModal(false);
      return;
    }

    // Reset popup timer on route change (only for non-authenticated users)
    const popupTimer = setTimeout(() => {
      setShowApplicationModal(true);
    }, 1500);

    return () => {
      clearTimeout(popupTimer);
    };
  }, [location.pathname, setShowApplicationModal]);

  // Listen for authentication changes
  useEffect(() => {
    const handleAuthChange = () => {
      const isAuthenticated = sessionStorage.getItem('_auth_token_') !== null;
      if (isAuthenticated) {
        setShowApplicationModal(false);
      }
    };

    // Listen for login success and storage changes
    window.addEventListener('loginSuccess', handleAuthChange);
    window.addEventListener('storage', (e) => {
      if (e.key === '_auth_token_') {
        handleAuthChange();
      }
    });

    return () => {
      window.removeEventListener('loginSuccess', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, [setShowApplicationModal]);

  return (
    <ApplicationForm 
      show={showApplicationModal} 
      onHide={() => setShowApplicationModal(false)} 
    />
  );
};

function App() {
  const [isLoading, setIsLoading] = React.useState(true);
  const [showApplicationModal, setShowApplicationModal] = React.useState(false);

  useEffect(() => {
    // Initialize all libraries after component mounts
    initAllLibraries();
    
    // Initialize URL parameters service to detect ref parameter on any page
    urlParamsService.refreshPartnerId();
    
    // Listen for custom events to show application modal
    const handleShowApplicationModal = () => {
      setShowApplicationModal(true);
    };
    
    window.addEventListener('showApplicationModal', handleShowApplicationModal);
    
    // Hide loading screen after a short delay
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('showApplicationModal', handleShowApplicationModal);
    };
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <ScrollToTopOnRouteChange />
      <div className="App">
        <Header />
        <main className="main">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/privacy-policy-and-disclaimer" element={<PrivacyPolicy />} />
            <Route path="/mewar-faqs" element={<FAQs />} />
            <Route path="/recognitions-achievements" element={<RecognitionsAchievements />} />
            <Route path="/login" element={<Login />} />
            <Route path="/application-form" element={
              <ProtectedRoute>
                <ApplicationFormPage />
              </ProtectedRoute>
            } />
            <Route path="/payment-history" element={
              <ProtectedRoute>
                <PaymentPage />
              </ProtectedRoute>
            } />
            <Route path="/course-fees" element={
              <ProtectedRoute>
                <CourseFeesPage />
              </ProtectedRoute>
            } />
            
            {/* B.Tech Routes */}
            <Route path="/btech-civil-engineering" element={<BTechCivilEngineering />} />
            <Route path="/btech-computer-science-engineering" element={<BTechComputerScienceEngineering />} />
            <Route path="/btech-electrical-engineering" element={<BTechElectricalEngineering />} />
            <Route path="/btech-electronic-and-communication-engneering" element={<BTechElectronicAndCommunicationEngineering />} />
            <Route path="/btech-chemical-engneering" element={<BTechChemicalEngineering />} />
            <Route path="/btech-mechanical-engineering" element={<BTechMechanicalEngineering />} />
            <Route path="/btech-mining-engneering" element={<BTechMiningEngineering />} />
            
            {/* M.Tech Routes */}
            <Route path="/mtech-in-civil-engineering" element={<MTechCivilEngineering />} />
            <Route path="/mtech-in-computer-science-engineering" element={<MTechComputerScienceEngineering />} />
            <Route path="/mtech-in-electrical-engineering" element={<MTechElectricalEngineering />} />
            <Route path="/mtech-in-electronics-communication-engineering" element={<MTechElectronicsCommunicationEngineering />} />
            <Route path="/mtech-in-chemical-engineering" element={<MTechChemicalEngineering />} />
            <Route path="/mtech-in-mechanical-engineering" element={<MTechMechanicalEngineering />} />
            <Route path="/mtech-in-mining-engineering" element={<MTechMiningEngineering />} />
          </Routes>
        </main>
        <Footer />
        <ScrollToTop />
        
        {/* Global Application Modal Handler */}
        <ApplicationFormHandler 
          showApplicationModal={showApplicationModal}
          setShowApplicationModal={setShowApplicationModal}
        />
      </div>
    </Router>
  );
}

export default App;